// components/PDFPreview.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tooltip,
  Stack,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Paper,
  Typography,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomIn from "@mui/icons-material/ZoomIn";
import ZoomOut from "@mui/icons-material/ZoomOut";
import RestartAlt from "@mui/icons-material/RestartAlt";
import FirstPage from "@mui/icons-material/FirstPage";
import LastPage from "@mui/icons-material/LastPage";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import FitScreen from "@mui/icons-material/FitScreen";
import { Document, Page, pdfjs } from "react-pdf";
import Loader from "Atoms/Loader";

// Keep worker in sync with your react-pdf/pdf.js version
pdfjs.GlobalWorkerOptions.workerSrc =
  "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/5.3.93/pdf.worker.min.mjs";

/** Common props (always present) */
type BaseProps = {
  /** 'dialog' | 'inline' — default: 'dialog' */
  variant?: "dialog" | "inline";
  /** PDF URL (signed or public) */
  fileUrl: string | null;
  /** 1-based page number (initial or externally controlled) */
  pageNumber?: number | null;
  /** start zoom */
  initialScale?: number;
  /** show text/annotation layers (off by default for perf) */
  renderLayers?: boolean;
  /** called when total pages known */
  onPages?: (numPages: number) => void;
};

type DialogOnly = {
  open: boolean;
  onClose: () => void;
  title?: string;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  fullWidth?: boolean;
};

type InlineOnly = {
  showHeader?: boolean;
  height?: number | string;
  sx?: any;
};

type Props =
  | (BaseProps & { variant?: "dialog" } & DialogOnly)
  | (BaseProps & { variant: "inline" } & InlineOnly);

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const STEP_SCALE = 0.1;
const DEFAULT_SCALE = 1.1;

export default function PDFPreview(props: Props) {
  const {
    variant = "dialog",
    fileUrl,
    pageNumber = 1,
    initialScale = DEFAULT_SCALE,
    renderLayers = false,
    onPages,
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(initialScale);
  const [fitToWidth, setFitToWidth] = useState(false);
  const [localPage, setLocalPage] = useState<number>(pageNumber || 1);

  const isDialog = variant === "dialog";

  const currentPage = useMemo(
    () => Math.max(1, Math.min(localPage || 1, numPages || Infinity)),
    [localPage, numPages]
  );

  useEffect(() => {
    setNumPages(null);
    setScale(initialScale);
    setFitToWidth(false);
    setLocalPage(pageNumber || 1);
  }, [fileUrl]);

  useEffect(() => {
    if (pageNumber) setLocalPage(pageNumber);
  }, [pageNumber]);

  useEffect(() => {
    const enabled = isDialog ? (props as any).open : true;
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") setLocalPage((p) => p + 1);
      if (e.key === "ArrowLeft") setLocalPage((p) => p - 1);
      if (e.key === "+" || e.key === "=")
        setScale((s) => Math.min(MAX_SCALE, +(s + STEP_SCALE).toFixed(2)));
      if (e.key === "-")
        setScale((s) => Math.max(MIN_SCALE, +(s - STEP_SCALE).toFixed(2)));
      if (e.key === "0") setScale(initialScale);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDialog, (props as any).open, initialScale]);

  useEffect(() => {
    if (!fitToWidth) return;
    const container = containerRef.current;
    if (!container) return;
    const id = requestAnimationFrame(() => {
      const pageCanvas = container.querySelector("canvas");
      if (!pageCanvas) return;
      const available = container.clientWidth - 24;
      const intrinsic = (pageCanvas as HTMLCanvasElement).width / scale;
      if (intrinsic > 0) {
        const newScale = Math.max(
          MIN_SCALE,
          Math.min(MAX_SCALE, +(available / intrinsic).toFixed(2))
        );
        setScale(newScale);
      }
    });
    return () => cancelAnimationFrame(id);
  }, [fitToWidth, numPages, currentPage, scale]);

  const goFirst = () => setLocalPage(1);
  const goPrev = () => setLocalPage((p) => p - 1);
  const goNext = () => setLocalPage((p) => p + 1);
  const goLast = () => numPages && setLocalPage(numPages);
  const zoomOut = () =>
    setScale((s) => Math.max(MIN_SCALE, +(s - STEP_SCALE).toFixed(2)));
  const zoomIn = () =>
    setScale((s) => Math.min(MAX_SCALE, +(s + STEP_SCALE).toFixed(2)));
  const resetZoom = () => setScale(initialScale);

  const renderPagePicker = () => {
    if (!numPages) return null;
    if (numPages <= 200) {
      const items = Array.from({ length: numPages }, (_, i) => i + 1);
      return (
        <Select
          size="small"
          value={currentPage}
          onChange={(e) => setLocalPage(Number(e.target.value))}
          sx={{ minWidth: 120 }}
        >
          {items.map((p) => (
            <MenuItem value={p} key={p}>
              Page {p}
            </MenuItem>
          ))}
        </Select>
      );
    }
    return (
      <TextField
        size="small"
        type="number"
        value={currentPage}
        onChange={(e) => {
          const val = Math.max(
            1,
            Math.min(Number(e.target.value || 1), numPages)
          );
          setLocalPage(val);
        }}
        InputProps={{
          inputProps: { min: 1, max: numPages },
          endAdornment: (
            <InputAdornment position="end">/ {numPages}</InputAdornment>
          ),
        }}
        sx={{ width: 130 }}
      />
    );
  };

  const Toolbar = (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      <Tooltip title="First page">
        <span>
          <IconButton
            size="small"
            onClick={goFirst}
            disabled={!numPages || currentPage === 1}
          >
            <FirstPage />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Previous page">
        <span>
          <IconButton
            size="small"
            onClick={goPrev}
            disabled={!numPages || currentPage <= 1}
          >
            <ChevronLeft />
          </IconButton>
        </span>
      </Tooltip>

      {renderPagePicker()}

      <Tooltip title="Next page">
        <span>
          <IconButton
            size="small"
            onClick={goNext}
            disabled={!numPages || (!!numPages && currentPage >= numPages)}
          >
            <ChevronRight />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Last page">
        <span>
          <IconButton
            size="small"
            onClick={goLast}
            disabled={!numPages || (!!numPages && currentPage === numPages)}
          >
            <LastPage />
          </IconButton>
        </span>
      </Tooltip>

      <Stack direction="row" alignItems="center" sx={{ ml: 1 }}>
        <Tooltip title="Fit to width">
          <IconButton
            size="small"
            color={fitToWidth ? "primary" : "default"}
            onClick={() => setFitToWidth((f) => !f)}
          >
            <FitScreen />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom out">
          <span>
            <IconButton
              size="small"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
            >
              <ZoomOut />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Reset zoom">
          <IconButton size="small" onClick={resetZoom}>
            <RestartAlt />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom in">
          <span>
            <IconButton
              size="small"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
            >
              <ZoomIn />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Box>
  );

  const Viewer = (
    <Box ref={containerRef} sx={{ bgcolor: "#f7f7f7", overflowX: "hidden" }}>
      {!fileUrl ? (
        <Box sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
          No file to preview.
        </Box>
      ) : (
        <Document
        
          file={fileUrl}
          loading={<Loader variant="inline" text="Loading PDF..." color="primary" />}
          error={
            <Box sx={{ p: 4, color: "error.main" }}>Failed to load PDF.</Box>
          }
          onLoadSuccess={({ numPages }) => {
            setNumPages(numPages);
            onPages?.(numPages);
            setLocalPage((p) => Math.max(1, Math.min(p, numPages)));
          }}
        >
          <Page
            key={`${fileUrl}-${currentPage}-${scale}-${fitToWidth}`}
            pageNumber={currentPage}
            scale={scale}
            width={
              fitToWidth
                ? containerRef.current?.clientWidth ?? undefined
                : undefined
            }
            renderAnnotationLayer={renderLayers}
            renderTextLayer={renderLayers}
            loading={
              <Box sx={{ p: 4, textAlign: "center" }}>Rendering page…</Box>
            }
          />
        </Document>
      )}
    </Box>
  );

  if (isDialog) {
    const {
      open,
      onClose,
      title = "Document preview",
      maxWidth = "lg",
      fullWidth = true,
    } = props as DialogOnly & { variant: "dialog" } & BaseProps;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={maxWidth}
        fullWidth={fullWidth}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {title}
          <Box
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            <IconButton size="small" onClick={onClose} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {Viewer}
        </DialogContent>
        <DialogActions>{Toolbar}</DialogActions>
      </Dialog>
    );
  }

  const {
    showHeader = true,
    height = 560,
    sx,
  } = props as InlineOnly & { variant: "inline" } & BaseProps;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, ...sx }}>
      {showHeader && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ opacity: 0.75 }}>
            PDF Preview
          </Typography>
        </Box>
      )}
      <Box sx={{ height, overflow: "auto" }}>{Viewer}</Box>
      <Box sx={{ display:"flex",justifyContent:"end" }}>{Toolbar}</Box>
    </Paper>
  );
}
